import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { ScanLine } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { useI18n } from "@/i18n/I18n";

// Each new scanner waits until the previous one has fully stopped (StrictMode remount, quick re-entry)
let stopping = Promise.resolve();

export default function ScanPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const done = useRef(false);
  const lastInvalid = useRef(0);
  // Latest t/navigate via ref so a language change doesn't restart the camera
  const latest = useRef({ t, navigate });
  useLayoutEffect(() => { latest.current = { t, navigate }; }, [t, navigate]);

  useEffect(() => {
    let scanner = null;
    let cancelled = false;
    const onScan = (text) => {
      if (done.current) return;
      const m = text.match(/\/l\/([a-f0-9]{32})/);
      if (!m) {
        if (Date.now() - lastInvalid.current > 3000) { lastInvalid.current = Date.now(); toast.error(latest.current.t("scan.invalid")); }
        return;
      }
      done.current = true;
      latest.current.navigate(`/locations/${m[1]}`);
    };
    const started = stopping.then(() => {
      if (cancelled) return undefined;
      scanner = new Html5Qrcode("qr-reader");
      return scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, onScan, () => {});
    }).catch(() => { if (!cancelled) setError(latest.current.t("scan.noCamera")); });
    return () => {
      cancelled = true;
      stopping = started.then(() => (scanner?.isScanning ? scanner.stop() : undefined)).catch(() => {});
    };
  }, []);

  return (
    <div className="max-w-md mx-auto">
      <PageHeader title={t("scan.title")} subtitle={t("scan.hint")} testId="scan-title" />
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 aspect-square">
        <div id="qr-reader" data-testid="qr-reader" className="w-full h-full [&_video]:object-cover [&_video]:h-full" />
        {error && (
          <div data-testid="scan-error" className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-8 text-center text-white">
            <ScanLine className="w-10 h-10 text-amber-400" /><p className="text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
